/**
 * Date helpers.
 *
 * A "date key" is a local-time `YYYY-MM-DD` string. We deliberately avoid
 * `Date#toISOString()` because that converts to UTC and can shift the day
 * for users west/east of Greenwich.
 */

export const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export const DAYS_IN_WEEK = 7;

/** Zero-pad a number to two digits. */
const pad = (n) => String(n).padStart(2, '0');

/**
 * Convert a Date to a local `YYYY-MM-DD` key.
 * @param {Date} date
 * @returns {string}
 */
export function toDateKey(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * Parse a `YYYY-MM-DD` key back into a local Date at midnight.
 * @param {string} key
 * @returns {Date}
 */
export function parseDateKey(key) {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/**
 * Strip the time component, returning a new Date at local midnight.
 * @param {Date} date
 * @returns {Date}
 */
export function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Add (or subtract, with a negative value) whole days.
 * @param {Date} date
 * @param {number} amount
 * @returns {Date}
 */
export function addDays(date, amount) {
  const next = startOfDay(date);
  next.setDate(next.getDate() + amount);
  return next;
}

/**
 * Whole days from `from` to `to`, ignoring the time of day.
 *
 * Rounding is deliberate: a DST boundary makes the span 23 or 25 hours, and
 * without it a plain division would land just short of the whole day.
 *
 * @param {Date} from
 * @param {Date} to
 * @returns {number} negative when `to` is earlier than `from`
 */
export function daysBetween(from, to) {
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  return Math.round((startOfDay(to) - startOfDay(from)) / MS_PER_DAY);
}

/**
 * Milliseconds until the next local midnight.
 *
 * A habit tracker is often left open on a phone overnight, so "today" has to
 * be able to move on its own rather than being fixed at page load.
 *
 * @param {Date} [now]
 * @returns {number} always at least 1, so a timer can never spin at zero
 */
export function msUntilMidnight(now = new Date()) {
  const midnight = addDays(startOfDay(now), 1);
  return Math.max(1, midnight.getTime() - now.getTime());
}

/**
 * Monday-based weekday index: Mon = 0 ... Sun = 6.
 * @param {Date} date
 * @returns {number}
 */
export function weekdayIndex(date) {
  return (date.getDay() + 6) % DAYS_IN_WEEK;
}

/**
 * The Monday of the week containing `date`.
 * @param {Date} date
 * @returns {Date}
 */
export function startOfWeek(date) {
  return addDays(date, -weekdayIndex(date));
}

/**
 * All seven Dates of the week containing `date`, Monday first.
 * @param {Date} date
 * @returns {Date[]}
 */
export function getWeekDays(date) {
  const monday = startOfWeek(date);
  return Array.from({ length: DAYS_IN_WEEK }, (_, i) => addDays(monday, i));
}

/**
 * @param {Date} a
 * @param {Date} b
 * @returns {boolean} true when both fall on the same calendar day
 */
export function isSameDay(a, b) {
  return toDateKey(a) === toDateKey(b);
}

/**
 * @param {Date} date
 * @param {Date} [today]
 * @returns {boolean}
 */
export function isToday(date, today = new Date()) {
  return isSameDay(date, today);
}

/**
 * @param {Date} date
 * @param {Date} [today]
 * @returns {boolean} true when the date is strictly before today
 */
export function isPast(date, today = new Date()) {
  return startOfDay(date) < startOfDay(today);
}

